import { NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import * as fromSeriousSystem from '@serious-system';
import { ThinkingStep } from '@shared';
import { mcpMarkFor } from '../../../connectors/connectors.models';

/** Artifact beat: a group that has finished folds itself this long after. */
const GROUP_FOLD_DELAY_MS = 250;
/** The header label fades out, swaps, and fades back in (`--motion-swap`). */
const LABEL_SWAP_MS = 150;
/**
 * A bare hostname — the only thing a `page` step's `detail` may carry.
 * Anything else renders as plain text: no link, no favicon request.
 */
const HOSTNAME =
  /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i;
/**
 * Stands in for a row's dark part inside its translated sentence, so the
 * sentence decides where that part goes (EN "…for the {emph} skill", FR "…de
 * l’expertise {emph}"). A private-use character: no text ever carries it.
 */
const EMPH = '\uE000';
/**
 * Government sites: the only pages a row calls official (the labelling doc's
 * "Read the official page on…"), and the only ones with their own icon — shipped
 * with the app (`assets/official-sites`), so the reader's browser never calls a
 * site the model picked, and the icon is there even where a site serves none at
 * `/favicon.ico`. `null`: official, but no icon could be fetched — the globe.
 * The word is a claim, so it is only made where it can't be wrong.
 * ponytail: a fixed list — Canada's governments, then gov domains elsewhere; a
 * per-source flag from core-ai if other official sites ever need the word.
 */
const OFFICIAL_SITES: Readonly<Record<string, string | null>> = {
  'canada.ca': 'canada',
  'gc.ca': 'canada',
  'quebec.ca': 'quebec',
  'gouv.qc.ca': 'quebec',
  'revenuquebec.ca': 'revenu-quebec',
  'ontario.ca': 'ontario',
  'alberta.ca': 'alberta',
  'saskatchewan.ca': 'saskatchewan',
  'novascotia.ca': 'nova-scotia',
  'princeedwardisland.ca': null,
  'yukon.ca': null,
  'gnb.ca': 'new-brunswick',
  'gov.bc.ca': 'british-columbia',
  'gov.mb.ca': 'manitoba',
  'gov.nl.ca': 'newfoundland',
  'gov.nt.ca': 'northwest-territories',
  'gov.nu.ca': null,
  gov: null,
  'gouv.fr': null,
  'gov.uk': 'united-kingdom',
};

/** Numbers each trace, for the ids that tie its header to its steps. */
let nextTraceId = 0;

/** The official site *host* belongs to, if it is one. */
const officialSite = (host: string): string | undefined =>
  Object.keys(OFFICIAL_SITES).find(
    (domain) => host === domain || host.endsWith(`.${domain}`),
  );

const officialHost = (host: string): boolean =>
  officialSite(host) !== undefined;

/**
 * One row of the trace: a step of its own, or a run of consecutive searches or
 * documents — a lone row until a second one lands, then a group (Figma
 * "Grouped=True") whose sub-steps are the run's rows.
 */
interface ReflexionRow {
  key: string;
  steps: ThinkingStep[];
}

/**
 * One row's words (Figma "Thinking – Step"): a grey sentence holding the ONE
 * dark part — a purpose, a source name or a query — where its {emph} sits.
 * `text` is words core-ai wrote for the row (a reasoning title, what a lone
 * search checked, what a lone document is) and wins over the i18n `key`.
 */
interface RowText {
  key: string;
  text: string | null;
  emph: string | null;
}

/** What the header shows; lags the inputs by {@link LABEL_SWAP_MS} on a change. */
interface HeaderView {
  done: boolean;
  summary: string | null;
}

/**
 * SB-3651 — the reasoning trace (Figma "Thinking", "Thinking – Step",
 * "Thinking – Sub-Step", "Thinking – Footer"): a header ticking « Réflexion
 * 19 s » while the teammate works, one row per {@link ThinkingStep} in its
 * in-progress wording, then the past tense once done (the labelling doc's
 * rules). Searches and documents fold under a count header from two in a row
 * (their raw queries and file names show as sub-steps); a finished group
 * folds itself.
 *
 * Thinking is over the moment the answer's first words land: the trace
 * collapses and its header becomes the one-line summary (« Réfléchi pendant
 * 19 s » until one exists), with the duration moved to the footer. Used live
 * by `conversation.page.ts` and, for every saved reply, by the messages
 * listing (`live=false`, already collapsed).
 */
@Component({
  selector: 'squadbox-reflexion-block',
  imports: [
    TranslateModule,
    NgTemplateOutlet,
    fromSeriousSystem.UseArrowIconDirective,
    fromSeriousSystem.UseAssistantCustomIconDirective,
    fromSeriousSystem.UseFileTypeIconDirective,
    fromSeriousSystem.UseHeroIconOutlineDirective,
    fromSeriousSystem.UseMcpIconDirective,
    fromSeriousSystem.UseTeammateIconDirective,
    fromSeriousSystem.UseUIBasicIconDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ng-template #chevron>
      <svg
        sdUseArrowIcon="chevron-small-down"
        class="chevron-icon"
        aria-hidden="true"
      ></svg>
    </ng-template>

    <ng-template #fileIcon let-type="type">
      @if (type === 'default') {
        <svg sdUseAssistantCustomIcon="document" aria-hidden="true"></svg>
      } @else {
        <svg [sdUseFileTypeIcon]="type" aria-hidden="true"></svg>
      }
    </ng-template>

    <ng-template #stepIcon let-step="step">
      @switch (step.kind) {
        @case ('reasoning') {
          <svg sdUseAssistantCustomIcon="bulb" aria-hidden="true"></svg>
        }
        @case ('web_search') {
          <svg sdUseUIBasicIcon="search-xs" aria-hidden="true"></svg>
        }
        @case ('document') {
          <ng-container
            [ngTemplateOutlet]="fileIcon"
            [ngTemplateOutletContext]="{ type: fileType(step.detail) }"
          />
        }
        @case ('page') {
          @let host = pageHost(step);
          @let icon = host ? officialIcon(host) : null;
          @if (icon && !brokenFavicons().has(icon)) {
            <img
              [src]="icon"
              alt=""
              width="16"
              height="16"
              (error)="markFaviconBroken(icon)"
            />
          } @else {
            <svg sdUseHeroIconOutline="globe-alt" aria-hidden="true"></svg>
          }
        }
        @case ('chart') {
          <svg sdUseTeammateIcon="dashboard-panel" aria-hidden="true"></svg>
        }
        @case ('connector') {
          <!-- The vendor's mark from the catalog id, as the connectors modal draws it;
               a vendor the sprite does not ship keeps the neutral dot. -->
          @let mark = mcpMark(step.detail);
          @if (mark) {
            <svg [sdUseMcpIcon]="mark" aria-hidden="true"></svg>
          } @else {
            <i class="other-dot"></i>
          }
        }
        @case ('skill') {
          <!-- ponytail: Figma's "scroll" glyph has no design-system sprite
               yet, so it stays inline until one is published. -->
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path
              fill="currentColor"
              d="M12.5 10.5H12V4C12 2.897 11.103 2 10 2H3.75C2.785 2 2 2.785 2 3.75V4C2 4.827 2.673 5.5 3.5 5.5H4.5V12.25C4.5 13.2145 5.2845 13.9995 6.2495 14H12.25C13.215 14 14 13.215 14 12.25V12C14 11.173 13.327 10.5 12.5 10.5ZM4.5 4.5H3.5C3.2245 4.5 3 4.2755 3 4V3.75C3 3.3365 3.3365 3 3.75 3C4.1635 3 4.5 3.3365 4.5 3.75V4.5ZM7 12V12.25C7 12.6635 6.6635 13 6.25 13C5.8365 13 5.5 12.6635 5.5 12.25V3.75C5.5 3.4805 5.4335 3.228 5.3245 3H10C10.5515 3 11 3.4485 11 4V10.5H8.5C7.673 10.5 7 11.173 7 12ZM13 12.25C13 12.6635 12.6635 13 12.25 13H7.831C7.939 12.7725 8 12.5185 8 12.25V12C8 11.7245 8.2245 11.5 8.5 11.5H12.5C12.7755 11.5 13 11.7245 13 12V12.25Z"
            />
          </svg>
        }
        @default {
          <i class="other-dot"></i>
        }
      }
    </ng-template>

    <ng-template #rowWords let-step="step" let-sub="sub" let-running="running">
      @let words = rowText(step, sub, running);
      @let host = pageHost(step);
      @let line =
        around(words.text ?? (words.key | translate: { emph: emphMark }));
      <span class="txt">
        <span>{{ line.before }}</span>
        @if (words.emph) {
          @if (host) {
            <a
              class="emph"
              [href]="'https://' + host"
              target="_blank"
              rel="noopener noreferrer"
              >{{ host }}</a
            >
          } @else {
            <span class="emph">{{ words.emph }}</span>
          }
        }
        @if (line.after) {
          <span class="after">{{ line.after }}</span>
        }
      </span>
    </ng-template>

    <ng-template #headLabel>
      <span class="label" [class.swap]="swapping()">
        @if (!shownHeader().done) {
          <span>{{ 'THINKING.HEADER_LIVE' | translate }}</span>
          <span>{{
            'THINKING.SECONDS' | translate: { seconds: seconds() }
          }}</span>
        } @else if (shownHeader().summary; as summary) {
          <span>{{ summary }}</span>
        } @else {
          <span>{{
            'THINKING.FOOTER' | translate: { seconds: seconds() }
          }}</span>
        }
      </span>
    </ng-template>

    <div
      class="reflexion"
      [class.collapsed]="collapsed()"
      [class.live]="live()"
    >
      @if (noSteps()) {
        <!-- The turn thought without taking a step: how long it thought, and
             nothing to open. -->
        <div class="reflexion-head static">
          <ng-container [ngTemplateOutlet]="headLabel" />
        </div>
      } @else {
        <button
          type="button"
          class="reflexion-head"
          [attr.aria-expanded]="!collapsed()"
          [attr.aria-controls]="stepsId"
          (click)="toggleCollapsed()"
        >
          <ng-container [ngTemplateOutlet]="headLabel" />
          <ng-container [ngTemplateOutlet]="chevron" />
        </button>
        <div class="expand" [id]="stepsId" [class.closed]="collapsed()">
          <div>
            <div class="steps">
              @for (row of rows(); track row.key; let last = $last) {
                <!-- One head element per row, whatever it holds: a lone search
                   turns into its group in place when a second one lands. -->
                @let grouped = row.steps.length > 1;
                @let head = row.steps[0];
                @let open = grouped && isGroupOpen(row);
                @let running = isRunning(last);
                @let groupLabel =
                  grouped
                    ? (groupKey(row, last)
                      | translate: { count: row.steps.length })
                    : '';
                @let purpose = grouped ? groupPurpose(row) : null;
                <div
                  class="step"
                  [class.active]="
                    grouped
                      ? running && lastGroupOpen()
                      : head.id === activeStepId()
                  "
                  [attr.role]="grouped ? 'button' : null"
                  [attr.tabindex]="grouped ? 0 : null"
                  [attr.aria-expanded]="grouped ? open : null"
                  [attr.title]="
                    grouped
                      ? purpose
                        ? groupLabel + ' ' + purpose
                        : groupLabel
                      : rowTitle(head, running)
                  "
                  (click)="toggleGroup(row)"
                  (keydown.enter)="toggleGroup(row)"
                  (keydown.space)="onSpace(row, $event)"
                >
                  <span class="ico">
                    @if (!grouped) {
                      <ng-container
                        [ngTemplateOutlet]="stepIcon"
                        [ngTemplateOutletContext]="{ step: head }"
                      />
                    } @else if (head.kind === 'web_search') {
                      <svg
                        sdUseUIBasicIcon="search-xs"
                        aria-hidden="true"
                      ></svg>
                    } @else {
                      <ng-container
                        [ngTemplateOutlet]="fileIcon"
                        [ngTemplateOutletContext]="{ type: groupFileType(row) }"
                      />
                    }
                  </span>
                  @if (grouped) {
                    <span class="txt">
                      <span>{{ groupLabel }}</span>
                      @if (purpose) {
                        <span class="emph">{{ purpose }}</span>
                      }
                    </span>
                    <ng-container [ngTemplateOutlet]="chevron" />
                  } @else {
                    <ng-container
                      [ngTemplateOutlet]="rowWords"
                      [ngTemplateOutletContext]="{
                        step: head,
                        sub: false,
                        running,
                      }"
                    />
                  }
                </div>
                @if (grouped) {
                  <div class="expand" [class.closed]="!open">
                    <div>
                      <div class="sub-steps">
                        @for (
                          step of row.steps;
                          track step.id;
                          let lastSub = $last
                        ) {
                          <!-- Only the newest search or file of a running group is
                             in progress: each one before it is done. -->
                          <div
                            class="step"
                            [class.active]="running && lastSub"
                            [attr.title]="
                              rowTitle(step, running && lastSub, true)
                            "
                          >
                            <span class="ico">
                              <ng-container
                                [ngTemplateOutlet]="stepIcon"
                                [ngTemplateOutletContext]="{ step }"
                              />
                            </span>
                            <ng-container
                              [ngTemplateOutlet]="rowWords"
                              [ngTemplateOutletContext]="{
                                step,
                                sub: true,
                                running: running && lastSub,
                              }"
                            />
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                }
              }
            </div>
            @if (shownHeader().done && shownHeader().summary) {
              <div class="footer">
                {{ 'THINKING.FOOTER' | translate: { seconds: seconds() } }}
              </div>
            }
          </div>
        </div>
      }
    </div>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
    }

    .reflexion {
      display: flex;
      flex-direction: column;
      padding: 4px 8px;
    }

    .reflexion-head {
      display: flex;
      align-items: center;
      gap: 4px;
      width: 100%;
      padding: 6px 0;
      border: 0;
      border-bottom: 1px solid var(--color-neutral-200);
      background: none;
      font: inherit;
      font-size: 14px;
      line-height: 20px;
      text-align: left;
      color: var(--color-neutral-500);
      cursor: pointer;
      user-select: none;
    }

    .reflexion-head.static {
      cursor: default;
    }

    .label {
      display: flex;
      gap: 4px;
      min-width: 0;
      transition: opacity var(--motion-swap) var(--motion-ease);
    }

    .label.swap {
      opacity: 0;
    }

    .label span,
    .txt > * {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .reflexion-head:focus-visible,
    .step[role='button']:focus-visible {
      outline: 2px solid var(--color-primary-400);
      outline-offset: -2px;
      border-radius: 6px;
    }

    .reflexion-head:focus-visible {
      outline-offset: 2px;
      border-radius: 4px;
    }

    .chevron-icon {
      width: 20px;
      height: 20px;
      flex: 0 0 20px;
      color: var(--color-neutral-400);
      transition: transform var(--motion-base) var(--motion-ease);
    }

    .step .chevron-icon {
      margin-left: -4px;
    }

    .collapsed .reflexion-head .chevron-icon,
    .step[aria-expanded='false'] .chevron-icon {
      transform: rotate(-90deg);
    }

    .expand {
      display: grid;
      grid-template-rows: 1fr;
      transition: grid-template-rows var(--motion-base) var(--motion-ease);
    }

    .expand.closed {
      grid-template-rows: 0fr;
    }

    .expand > div {
      overflow: hidden;
      min-height: 0;
    }

    .steps,
    .sub-steps {
      display: flex;
      flex-direction: column;
    }

    .steps {
      padding-top: 8px;
    }

    .collapsed .steps {
      padding-top: 0;
    }

    /* Figma "Sub-steps": 24px indent, a 1px guide line at 15.5px. */
    .sub-steps {
      position: relative;
      padding-left: 24px;
    }

    .sub-steps::before {
      content: '';
      position: absolute;
      left: 15.5px;
      top: 0;
      bottom: 4px;
      width: 1px;
      background: var(--color-neutral-200);
    }

    .step {
      display: flex;
      align-items: center;
      gap: 8px;
      height: 32px;
      padding: 8px;
      font-size: 14px;
      line-height: 20px;
      color: var(--color-neutral-500);
      white-space: nowrap;
      overflow: hidden;
      animation: reflexion-step-in var(--motion-base) var(--motion-ease) both;
    }

    /* A saved trace takes over from the live one: its rows are already there. */
    .reflexion:not(.live) .step {
      animation: none;
    }

    .step[role='button'] {
      cursor: pointer;
      user-select: none;
    }

    .ico {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 16px;
      height: 16px;
      color: var(--color-neutral-400);
    }

    .ico > * {
      display: block;
      width: 16px;
      height: 16px;
    }

    .ico img {
      border-radius: 3px;
    }

    .ico .other-dot {
      width: 8px;
      height: 8px;
      border: 1px solid;
      border-radius: 8px;
    }

    .txt {
      display: flex;
      gap: 4px;
      min-width: 0;
    }

    /* The grey parts hold, the dark part ellipses (Figma "Content"). */
    .txt > :first-child:not(:last-child),
    .txt > .after {
      flex-shrink: 0;
    }

    .emph {
      color: var(--color-neutral-600);
    }

    a.emph {
      text-decoration: none;
      border-bottom: 1px solid transparent;
    }

    a.emph:hover {
      border-bottom-color: var(--color-neutral-400);
    }

    .step.active .txt > * {
      background: linear-gradient(
        90deg,
        var(--color-neutral-500) 35%,
        var(--color-neutral-700) 50%,
        var(--color-neutral-500) 65%
      );
      background-size: 220% 100%;
      -webkit-background-clip: text;
      background-clip: text;
      color: transparent;
      animation: reflexion-shimmer 1.6s linear infinite;
    }

    .step.active .ico {
      animation: reflexion-ico-pulse 1.6s ease-in-out infinite;
    }

    .footer {
      padding: 8px 0 6px 8px;
      border-bottom: 1px solid var(--color-neutral-200);
      font-size: 14px;
      line-height: 20px;
      color: var(--color-neutral-400);
    }

    @keyframes reflexion-step-in {
      from {
        opacity: 0;
        height: 0;
        padding-top: 0;
        padding-bottom: 0;
        transform: translateY(-4px);
      }
    }

    @keyframes reflexion-shimmer {
      from {
        background-position: 120% 0;
      }
      to {
        background-position: -120% 0;
      }
    }

    @keyframes reflexion-ico-pulse {
      50% {
        opacity: 0.45;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .step,
      .step.active .txt > *,
      .step.active .ico {
        animation: none;
      }
      .step.active .txt > * {
        color: var(--color-neutral-700);
        background: none;
      }
      .label,
      .chevron-icon,
      .expand {
        transition: none;
      }
    }
  `,
})
export class ReflexionBlockComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly translate = inject(TranslateService);

  public readonly steps = input.required<ThinkingStep[]>();
  public readonly startedAt = input.required<number>();
  public readonly answerStartedAt = input.required<number | null>();
  public readonly live = input.required<boolean>();
  /**
   * The model-written one-line summary of the finished trace — `null` until it
   * arrives, and for a trace saved before summaries existed. UNTRUSTED model
   * output: interpolated, never markup.
   */
  public readonly summary = input<string | null>(null);
  /**
   * False once the live turn has moved past the last row's group (the next
   * kind of step is about to show): the group then turns past tense and folds
   * before that step appears, as in the simulation.
   */
  public readonly lastGroupOpen = input(true);

  /** Thinking is over once the answer's first words land (or for a saved trace). */
  protected readonly done = computed(() => this.answerStartedAt() !== null);
  /** A finished trace with no step: only « Réfléchi pendant 5 s », no chevron. */
  protected readonly noSteps = computed(
    () => this.done() && this.steps().length === 0,
  );

  protected readonly emphMark = EMPH;

  /**
   * Follows {@link done} (see the constructor) — starts `false` because a
   * required input has no value yet at field-initializer time (NG8118).
   */
  protected readonly collapsed = signal(false);

  protected readonly shownHeader = signal<HeaderView>({
    done: false,
    summary: null,
  });
  protected readonly swapping = signal(false);

  /** Bundled site icons that failed to load: they fall back to the globe. */
  protected readonly brokenFavicons = signal<ReadonlySet<string>>(new Set());

  /** Groups that finished and folded themselves (the user can still reopen). */
  private readonly foldedGroups = signal<ReadonlySet<string>>(new Set());
  private readonly foldTimers = new Map<
    string,
    ReturnType<typeof setTimeout>
  >();

  private readonly manualGroupOpen = signal<ReadonlyMap<string, boolean>>(
    new Map(),
  );

  /** Re-ticks {@link seconds} once a second while the answer hasn't started. */
  private readonly tick = signal(0);

  protected readonly seconds = computed(() => {
    const answerStartedAt = this.answerStartedAt();
    if (answerStartedAt !== null) {
      return Math.max(
        1,
        Math.round((answerStartedAt - this.startedAt()) / 1000),
      );
    }
    this.tick();
    return Math.max(0, Math.floor((Date.now() - this.startedAt()) / 1000));
  });

  /** The step currently being worked on, if any — the last one `started`. */
  protected readonly activeStepId = computed(() => {
    if (!this.live() || this.done()) {
      return null;
    }
    const steps = this.steps();
    for (let i = steps.length - 1; i >= 0; i--) {
      if (steps[i].status === 'started') {
        return steps[i].id;
      }
    }
    return null;
  });

  /**
   * `steps()` folded into render rows: consecutive `web_search` or `document`
   * steps share one row — the runs core-ai words — and everything else is a row
   * of its own. A reasoning or other step without words has nothing to show.
   * A row's key is its first step's, so a run keeps its element as it grows;
   * the first row's is fixed, so the live turn's placeholder hands its element
   * to the first real row.
   */
  protected readonly rows = computed<ReflexionRow[]>(() => {
    const rows: ReflexionRow[] = [];
    for (const step of this.steps()) {
      if (
        (step.kind === 'reasoning' ||
          step.kind === 'other' ||
          step.kind === 'connector') &&
        !step.label.trim()
      ) {
        continue;
      }
      const previous = rows.at(-1);
      if (
        previous &&
        (step.kind === 'web_search' || step.kind === 'document') &&
        previous.steps[0].kind === step.kind
      ) {
        previous.steps.push(step);
      } else {
        rows.push({ key: rows.length ? step.id : 'first', steps: [step] });
      }
    }
    return rows;
  });

  /** Unique per trace: a page holds one per saved reply. */
  protected readonly stepsId = `reflexion-steps-${nextTraceId++}`;

  constructor() {
    // Interval only runs pre-answer; a fresh answerStartedAt tears it down.
    effect((onCleanup) => {
      if (this.answerStartedAt() !== null) {
        return;
      }
      const intervalId = setInterval(
        () => this.tick.update((v) => v + 1),
        1000,
      );
      onCleanup(() => clearInterval(intervalId));
    });

    // The trace folds the moment thinking ends (the artifact's "all at once")
    // and reopens if a later step takes the stream back after an
    // `answer_reset`. A click in between is the user's and holds until then.
    let wasDone: boolean | null = null;
    effect(() => {
      const done = this.done();
      if (done !== wasDone) {
        this.collapsed.set(done);
        wasDone = done;
      }
    });

    // Header cross-fade: « Réflexion 7 s » → the summary (or its fallback),
    // and fallback → summary when that arrives late. The first value shows
    // at once — a saved trace mounts with its summary, no fade.
    let headerMounted = false;
    effect((onCleanup) => {
      const done = this.done();
      const next: HeaderView = { done, summary: done ? this.summary() : null };
      const shown = untracked(this.shownHeader);
      const same = shown.done === next.done && shown.summary === next.summary;
      if (!headerMounted || same) {
        headerMounted = true;
        if (!same) {
          this.shownHeader.set(next);
        }
        this.swapping.set(false);
        return;
      }
      this.swapping.set(true);
      const timeoutId = setTimeout(() => {
        this.shownHeader.set(next);
        this.swapping.set(false);
      }, LABEL_SWAP_MS);
      onCleanup(() => clearTimeout(timeoutId));
    });

    // A group folds itself once it is no longer the one being worked on
    // (another step followed, or thinking ended) — at once for a saved trace.
    this.destroyRef.onDestroy(() => this.foldTimers.forEach(clearTimeout));
    effect(() => {
      const rows = this.rows();
      const live = this.live();
      const working = live && !this.done() && this.lastGroupOpen();
      const folded = untracked(this.foldedGroups);
      rows.forEach((row, index) => {
        if (
          row.steps.length < 2 ||
          folded.has(row.key) ||
          this.foldTimers.has(row.key) ||
          (working && index === rows.length - 1)
        ) {
          return;
        }
        if (live) {
          this.foldTimers.set(
            row.key,
            setTimeout(() => this.foldGroup(row.key), GROUP_FOLD_DELAY_MS),
          );
        } else {
          this.foldGroup(row.key);
        }
      });
    });
  }

  protected toggleCollapsed(): void {
    this.collapsed.update((collapsed) => !collapsed);
  }

  private foldGroup(key: string): void {
    this.foldTimers.delete(key);
    this.foldedGroups.update((keys) => new Set(keys).add(key));
  }

  protected isGroupOpen(row: ReflexionRow): boolean {
    return (
      this.manualGroupOpen().get(row.key) ?? !this.foldedGroups().has(row.key)
    );
  }

  protected toggleGroup(row: ReflexionRow): void {
    if (row.steps.length < 2) {
      return;
    }
    const next = !this.isGroupOpen(row);
    this.manualGroupOpen.update((map) => new Map(map).set(row.key, next));
  }

  protected onSpace(row: ReflexionRow, event: Event): void {
    if (row.steps.length > 1) {
      event.preventDefault();
      this.toggleGroup(row);
    }
  }

  protected markFaviconBroken(icon: string): void {
    this.brokenFavicons.update((icons) => new Set(icons).add(icon));
  }

  /** An official site's bundled icon, or `null` for the globe. */
  protected officialIcon(host: string): string | null {
    const site = officialSite(host);
    const name = site === undefined ? null : OFFICIAL_SITES[site];
    return name ? `/assets/official-sites/${name}.png` : null;
  }

  /** The last row of a live trace still being worked on. */
  protected isRunning(isLastRow: boolean): boolean {
    return isLastRow && this.live() && !this.done();
  }

  /**
   * A step's words (Figma "Thinking – Step" / "Sub-Step", the labelling doc):
   * its sentence in the in-progress form while it runs, in the past once done,
   * with the dark part where the sentence puts it. A lone search or document
   * shows the words core-ai wrote for it; a sub-step always shows its raw query
   * or file name, the group's row holding the purpose. `label` and `detail` are
   * untrusted model output — interpolated only.
   */
  protected rowText(
    step: ThinkingStep,
    sub: boolean,
    running: boolean,
  ): RowText {
    const phase = running && step.status === 'started' ? 'LIVE' : 'DONE';
    const detail = step.detail.trim();
    const label = step.label.trim();
    const named = (verb: string) =>
      `THINKING.STEP.${verb}${detail ? '' : '_UNNAMED'}_${phase}`;

    switch (step.kind) {
      case 'reasoning':
      case 'other':
      case 'connector':
        return { key: '', text: label, emph: null };
      case 'skill':
        return { key: named('SKILL'), text: null, emph: detail || null };
      case 'chart':
        return { key: named('CHART'), text: null, emph: detail || null };
      case 'web_search':
        if (!sub && label) {
          return { key: '', text: label, emph: null };
        }
        // A lone search with no wording yet says what it does; its query is a sub-step's.
        return !sub && phase === 'LIVE'
          ? { key: 'THINKING.STEP.SEARCH_UNNAMED_LIVE', text: null, emph: null }
          : { key: named('SEARCH'), text: null, emph: detail || null };
      case 'page': {
        const host = this.pageHost(step);
        const verb = host && officialHost(host) ? 'PAGE_OFFICIAL' : 'PAGE';
        return { key: named(verb), text: null, emph: detail || null };
      }
      case 'document':
        return !sub && label
          ? { key: '', text: label, emph: null }
          : { key: named('FILE'), text: null, emph: detail || null };
    }
  }

  /** A connector row's vendor mark, from its catalog id (`mcpMarkFor`). */
  protected mcpMark(connectorId: string) {
    return mcpMarkFor(connectorId.trim());
  }

  /** The row's words in one piece, for its tooltip. */
  protected rowTitle(
    step: ThinkingStep,
    running: boolean,
    sub = false,
  ): string {
    const words = this.rowText(step, sub, running);
    return (
      words.text ??
      String(this.translate.instant(words.key, { emph: words.emph ?? '' }))
    );
  }

  /** A translated sentence, split around its dark part (see {@link EMPH}). */
  protected around(line: string): { before: string; after: string } {
    const [before, after = ''] = line.split(EMPH);
    return { before: before.trim(), after: after.trim() };
  }

  /** In-progress wording while the group is the one being worked on. */
  protected groupKey(row: ReflexionRow, isLastRow: boolean): string {
    const running = this.isRunning(isLastRow) && this.lastGroupOpen();
    if (row.steps[0].kind === 'web_search') {
      return running
        ? 'THINKING.GROUP.SEARCHES_LIVE'
        : 'THINKING.GROUP.SEARCHES_DONE';
    }
    return running
      ? 'THINKING.GROUP.DOCUMENTS_LIVE'
      : 'THINKING.GROUP.DOCUMENTS_DONE';
  }

  /**
   * A group's shared purpose: the words core-ai put on its first row. Only a
   * search run has one; a document group reads as its template.
   */
  protected groupPurpose(row: ReflexionRow): string | null {
    return row.steps.map((step) => step.label.trim()).find(Boolean) ?? null;
  }

  /** Figma: the files' shared format, or Default for a mixed group. */
  protected groupFileType(
    row: ReflexionRow,
  ): fromSeriousSystem.FileTypeIconsEnum {
    const types = new Set(row.steps.map((step) => this.fileType(step.detail)));
    return types.size === 1
      ? [...types][0]
      : fromSeriousSystem.FileTypeIconsEnum.default;
  }

  /** The format icon of a file, read off its name as the user gave it. */
  protected fileType(name: string): fromSeriousSystem.FileTypeIconsEnum {
    const dot = name.lastIndexOf('.');
    return dot > 0
      ? fromSeriousSystem.getFileTypeIconFromExtension(name.slice(dot + 1))
      : fromSeriousSystem.FileTypeIconsEnum.default;
  }

  /**
   * A `page` step's site, when its `detail` is a bare hostname — without
   * `www.`, the way a person names it (core-ai drops it too; a trace saved
   * before it did still carries it).
   */
  protected pageHost(step: ThinkingStep): string | null {
    const detail = step.detail.trim();
    return step.kind === 'page' && HOSTNAME.test(detail)
      ? detail.toLowerCase().replace(/^www\./, '')
      : null;
  }
}
